import React from 'react'

function EstimateResult({ result }) {
  const {
    estimate_sqft_range,
    estimate_sqft_point,
    confidence,
    assumptions,
    stacking_plan,
    items_detected_summary,
    tips_for_accuracy,
    item_breakdown,
    calculation_notes,
    calculation_breakdown,
    disclaimer,
    next_steps_cta,
    config,
  } = result

  // Check if we're in development mode
  const isDev = import.meta.env.DEV

  // Get config values with defaults
  const maxStackLevels = config?.max_stack_levels || 3
  const overageFactor = config?.overage_factor || 1.15
  const overagePercent = Math.round((overageFactor - 1) * 100)

  // Debug logging for item breakdown
  console.log('isDev:', isDev, 'item_breakdown:', item_breakdown, 'config:', config)

  const confidenceColors = {
    low: 'bg-orange-100 text-orange-800',
    medium: 'bg-yellow-100 text-yellow-800',
    high: 'bg-green-100 text-green-800',
  }

  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden">
      {/* Header */}
      <div className="bg-blue-600 text-white px-6 py-4">
        <h2 className="text-xl font-bold">Your Storage Estimate</h2>
      </div>

      {/* Main Estimate */}
      <div className="px-6 py-6 border-b border-gray-200">
        <div className="text-center">
          <p className="text-4xl font-bold text-gray-900">
            {estimate_sqft_point} sq ft
          </p>
          <p className="text-gray-600 mt-1">
            Range: {estimate_sqft_range[0]} - {estimate_sqft_range[1]} sq ft
          </p>
          <span className={`inline-block mt-3 px-3 py-1 rounded-full text-sm font-medium ${confidenceColors[confidence]}`}>
            {confidence.charAt(0).toUpperCase() + confidence.slice(1)} Confidence
          </span>
        </div>
      </div>

      {/* Items Summary */}
      {items_detected_summary && (
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="font-semibold text-gray-900 mb-2">Items Detected</h3>
          <p className="text-gray-700">{items_detected_summary}</p>
        </div>
      )}

      {/* Stacking Plan */}
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 className="font-semibold text-gray-900 mb-3">Stacking Plan</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {stacking_plan.stackable_items?.length > 0 && (
            <div>
              <p className="text-sm font-medium text-gray-600 mb-1">Stackable Items</p>
              <ul className="text-sm text-gray-700 space-y-1">
                {stacking_plan.stackable_items.map((item, i) => (
                  <li key={i} className="flex items-start">
                    <span className="text-green-500 mr-2">+</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {stacking_plan.non_stackable_items?.length > 0 && (
            <div>
              <p className="text-sm font-medium text-gray-600 mb-1">Non-Stackable Items</p>
              <ul className="text-sm text-gray-700 space-y-1">
                {stacking_plan.non_stackable_items.map((item, i) => (
                  <li key={i} className="flex items-start">
                    <span className="text-gray-400 mr-2">-</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {stacking_plan.notes && (
          <p className="mt-3 text-sm text-gray-600 italic">{stacking_plan.notes}</p>
        )}
      </div>

      {/* Assumptions */}
      {assumptions?.length > 0 && (
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="font-semibold text-gray-900 mb-2">Assumptions</h3>
          <ul className="text-sm text-gray-700 space-y-1">
            {assumptions.map((assumption, i) => (
              <li key={i} className="flex items-start">
                <span className="text-blue-500 mr-2">*</span>
                {assumption}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Tips */}
      {tips_for_accuracy?.length > 0 && (
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="font-semibold text-gray-900 mb-2">Tips for a More Accurate Estimate</h3>
          <ul className="text-sm text-gray-700 space-y-1">
            {tips_for_accuracy.map((tip, i) => (
              <li key={i} className="flex items-start">
                <span className="text-yellow-500 mr-2">!</span>
                {tip}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Item Breakdown (Development Mode Only) */}
      {isDev && item_breakdown?.length > 0 && (
        <div className="px-6 py-4 border-b border-gray-200 bg-purple-50">
          <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
            <span className="bg-purple-600 text-white text-xs px-2 py-0.5 rounded mr-2">DEV</span>
            Item Breakdown
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-600 border-b border-purple-200">
                  <th className="pb-2 font-medium">Item</th>
                  <th className="pb-2 font-medium text-center">Qty</th>
                  <th className="pb-2 font-medium text-right">Sq Ft Each</th>
                  <th className="pb-2 font-medium text-right">Total Sq Ft</th>
                  <th className="pb-2 font-medium text-center">Stackable</th>
                </tr>
              </thead>
              <tbody>
                {item_breakdown.map((item, i) => (
                  <tr key={i} className="border-b border-purple-100 last:border-0">
                    <td className="py-2 text-gray-800">{item.item}</td>
                    <td className="py-2 text-center text-gray-700">{item.quantity}</td>
                    <td className="py-2 text-right text-gray-700">{item.sqft_each}</td>
                    <td className="py-2 text-right font-medium text-gray-800">{item.sqft_total}</td>
                    <td className="py-2 text-center">
                      {item.stackable ? (
                        <span className="text-green-600">Yes</span>
                      ) : (
                        <span className="text-gray-400">No</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-purple-200">
                  <td colSpan="3" className="pt-2 text-gray-600">Stackable (unstacked total)</td>
                  <td className="pt-2 text-right text-gray-600">
                    {item_breakdown.filter(i => i.stackable).reduce((sum, item) => sum + item.sqft_total, 0).toFixed(1)}
                  </td>
                  <td></td>
                </tr>
                <tr>
                  <td colSpan="3" className="text-gray-600">
                    Stackable floor ({calculation_breakdown?.stack_count || 0} stacks)
                  </td>
                  <td className="text-right text-gray-600">
                    {calculation_breakdown?.stackable_floor_sqft?.toFixed(1) || '0.0'}
                  </td>
                  <td></td>
                </tr>
                <tr>
                  <td colSpan="3" className="text-gray-600">Non-stackable</td>
                  <td className="text-right text-gray-600">
                    {calculation_breakdown?.non_stackable_sqft?.toFixed(1) || '0.0'}
                  </td>
                  <td></td>
                </tr>
                <tr className="border-t border-purple-200">
                  <td colSpan="3" className="pt-2 text-gray-600">Floor space</td>
                  <td className="pt-2 text-right text-gray-600">
                    {calculation_breakdown?.subtotal_sqft?.toFixed(1) || '0.0'}
                  </td>
                  <td></td>
                </tr>
                <tr>
                  <td colSpan="3" className="text-gray-600">Safety margin ({overagePercent}%)</td>
                  <td className="text-right text-gray-600">
                    {calculation_breakdown?.safety_margin_sqft?.toFixed(1) || '0.0'}
                  </td>
                  <td></td>
                </tr>
                <tr className="border-t-2 border-purple-300">
                  <td colSpan="3" className="pt-2 font-medium text-gray-800">Total</td>
                  <td className="pt-2 text-right font-bold text-purple-700">
                    {calculation_breakdown?.total_sqft?.toFixed(1) || estimate_sqft_point}
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
          {calculation_notes && (
            <div className="mt-4 p-3 bg-purple-100 rounded-lg">
              <p className="text-sm font-medium text-purple-800 mb-1">Calculation Notes:</p>
              <p className="text-sm text-purple-700">{calculation_notes}</p>
            </div>
          )}
        </div>
      )}

      {/* Disclaimer & CTA */}
      <div className="px-6 py-4 bg-gray-50">
        <p className="text-sm text-gray-600 mb-4">{disclaimer}</p>
        <button
          onClick={() => {
            // You can customize this to open Intercom, a modal, etc.
            alert('Contact functionality would be implemented here.')
          }}
          className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-blue-700 transition-colors"
        >
          {next_steps_cta}
        </button>
      </div>
    </div>
  )
}

export default EstimateResult
